import AccessTimeIcon from '@offline-mui/icons-material/AccessTime'
import ContentCopyIcon from '@offline-mui/icons-material/ContentCopy'
import FlightLandIcon from '@offline-mui/icons-material/FlightLand'
import FlightTakeoffIcon from '@offline-mui/icons-material/FlightTakeoff'
import OpenInNewIcon from '@offline-mui/icons-material/OpenInNew'
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@offline-mui/material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import type { InventoryFlight, InventoryItinerary, InventorySegment } from '../api/inventory'
import { FONT_MONO } from '../theme'
import {
  airlineName,
  airportLabel,
  fareBasisForFlight,
  formatDuration,
  formatShortDate,
  layoverMinutes,
  segmentFlights,
  totalFlightMinutes,
} from '../utils/flightFormat'
import { writeClipboard } from './CopyJsonButton'
import { FlightLegRow, LayoverRow, StopsChip } from './ui/FlightLegRow'

type ItineraryDirectionCardProps = {
  itinerary: InventoryItinerary
  segment: InventorySegment
  direction: 'Outbound' | 'Inbound'
}

function LegActions({
  itinerary,
  flight,
}: {
  itinerary: InventoryItinerary
  flight: InventoryFlight
}) {
  const [copied, setCopied] = useState<'flight' | 'fare' | null>(null)
  const fare = fareBasisForFlight(itinerary, flight)

  const flash = (key: 'flight' | 'fare') => {
    setCopied(key)
    window.setTimeout(() => setCopied(null), 1500)
  }

  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
      <Tooltip title={copied === 'flight' ? 'Copied' : 'Copy flight JSON'}>
        <IconButton
          size="small"
          aria-label="Copy flight JSON"
          onClick={() => {
            void writeClipboard(
              JSON.stringify({ itineraryId: itinerary.id, flight, fareBasis: fare }, null, 2),
            )
            flash('flight')
          }}
        >
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title={`Open flight ${flight.carrier} ${flight.flight_number}`}>
        <IconButton
          size="small"
          aria-label="Open flight record"
          component={RouterLink}
          to={`/offline-flight/flights?id=${flight.flight_id}`}
        >
          <OpenInNewIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

function FareCodeChip({
  code,
}: {
  code: string
}) {
  const [copied, setCopied] = useState(false)
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy fare basis code'}>
      <Chip
        size="small"
        variant="outlined"
        label={code}
        onClick={() => {
          void writeClipboard(code)
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1500)
        }}
        sx={{ fontFamily: FONT_MONO, fontWeight: 500, cursor: 'pointer' }}
      />
    </Tooltip>
  )
}

export default function ItineraryDirectionCard({
  itinerary,
  segment,
  direction,
}: ItineraryDirectionCardProps) {
  const flights = segmentFlights(segment)
  if (flights.length === 0) return null

  const first = flights[0]
  const last = flights[flights.length - 1]
  const stops = flights.length - 1
  const flightTime = totalFlightMinutes(flights)
  // Times are airport-local, so trip duration is flying time plus layovers
  // (each measured at one airport), never last arrival minus first departure.
  const tripTime =
    flightTime +
    flights.slice(1).reduce((sum, flight, index) => {
      const wait = layoverMinutes(flights[index], flight)
      return wait != null && wait > 0 ? sum + wait : sum
    }, 0)

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Chip
          size="small"
          color="primary"
          icon={direction === 'Outbound' ? <FlightTakeoffIcon /> : <FlightLandIcon />}
          label={direction}
        />
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          {first.departure_airport}
          <Box component="span" sx={{ color: 'text.disabled', mx: 0.6, fontWeight: 500 }}>
            →
          </Box>
          {last.arrival_airport}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formatShortDate(first.departure_datetime)}
          {tripTime > 0 ? ` · ${formatDuration(tripTime)} total` : ''}
        </Typography>
        <StopsChip stops={stops} />
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {airlineName(first.carrier)}
        </Typography>
      </Stack>

      <Box sx={{ flexGrow: 1 }}>
        {flights.map((flight, index) => {
          const previous = index > 0 ? flights[index - 1] : null
          const fare = fareBasisForFlight(itinerary, flight)
          const brand = fare?.brand_name || fare?.brand_code || null
          const operatedBy =
            flight.operating_carrier && flight.operating_carrier !== flight.carrier
              ? flight.operating_carrier_name || airlineName(flight.operating_carrier)
              : null

          return (
            <Box key={flight.flight_id}>
              {previous ? (
                <LayoverRow
                  minutes={layoverMinutes(previous, flight)}
                  airport={flight.departure_airport}
                />
              ) : null}
              <FlightLegRow
                carrier={flight.carrier}
                flightNumber={flight.flight_number}
                aircraft={flight.aircraft_type}
                origin={airportLabel(flight.departure_airport)}
                destination={airportLabel(flight.arrival_airport)}
                departureDatetime={flight.departure_datetime}
                arrivalDatetime={flight.arrival_datetime}
                durationMinutes={flight.duration_min ?? null}
                operatedBy={operatedBy}
                trailing={
                  <>
                    {fare?.cabin ? (
                      <Chip
                        size="small"
                        color="primary"
                        label={brand ? `${fare.cabin} · ${brand}` : fare.cabin}
                      />
                    ) : null}
                    {fare?.class_of_service ? (
                      <Chip size="small" variant="outlined" label={`Class ${fare.class_of_service}`} />
                    ) : null}
                    {fare?.fare_basis_code ? <FareCodeChip code={fare.fare_basis_code} /> : null}
                    <LegActions itinerary={itinerary} flight={flight} />
                  </>
                }
              />
            </Box>
          )
        })}
      </Box>

      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1,
          flexWrap: 'wrap',
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <AccessTimeIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Flight time {formatDuration(flightTime)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <AccessTimeIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Trip duration {formatDuration(tripTime)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}
